import { Injectable } from '@angular/core';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { DatePipe, formatDate } from '@angular/common';
import { CellPreparedEvent, ExportingEvent, Row } from 'devextreme/ui/data_grid';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
  providedIn: 'root',
})
export class ExportToExcelFileService {
  constructor(private translocoService: TranslocoService) {}
  // don't use with elasticSearch
  export(eventFromOnExporting: ExportingEvent, fileName: string): void | boolean {
    const workbook = new Workbook();
    const currentWorksheet = workbook.addWorksheet(fileName);

    exportDataGrid({
      component: eventFromOnExporting.component,
      worksheet: currentWorksheet,
      customizeCell(options) {
        const { gridCell, excelCell } = options;
        if (gridCell && gridCell.column && gridCell.rowType === 'data') {
          if (gridCell.column.cellTemplate === 'date') {
            const dp = new DatePipe('de');
            excelCell.value = dp.transform(excelCell.value, 'dd.MM.yyyy');
          } else if (Array.isArray(gridCell.value) && gridCell.value.length > 1) {
            // Apparently dev extreme cant export 1:n values by itself. Single item selections will be exported but multi item selections
            // will result in an empty cell. So this check will convert it from an array into a single string by using the own
            // calculateCellValue function so that it should work in all dataGrids out of the box.
            if (gridCell?.column?.lookup?.calculateCellValue) {
              excelCell.value = gridCell.value.reduce((result, currentCellValue) => {
                return [result, (gridCell?.column?.lookup?.calculateCellValue as (rowData: any) => any)(currentCellValue)]
                  .filter(Boolean)
                  .join(', ');
              }, '');
            }
          }
        }
      },
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), this.generateFileName(fileName));
      });
    });
    eventFromOnExporting.cancel = true;
  }

  /*
use this function if data in the CustomStore comes from ElasticSearch.
with the default export function, we don't know which rows are currently visible to the user otherwise
 */
  customExport(
    e: ExportingEvent,
    fileName: string,
    translationPrefix = '',
    dateFields: string[] = [],
    timeStampFields: string[] = []
  ): void {
    e.cancel = true;
    const visibleRows: Array<Row> = e.component.getVisibleRows();
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet();
    worksheet.getRow(1).font = { bold: true };
    const fieldsToFill: string[] = [];

    visibleRows.forEach((value, index) => {
      const rowdata = value.data;
      //table header
      if (index === 0) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore Property "cells" is missing in DevExtreme SourceCode definition but existing anyway
        value.cells.forEach((cell: CellPreparedEvent, number: number) => {
          if (cell.column.dataField) {
            fieldsToFill.push(cell.column.dataField);
            const translatedName = this.translocoService.translate(translationPrefix + cell.column.dataField);
            const headercol = worksheet.getColumn(number + 1);
            headercol.header = translatedName;
            headercol.width = translatedName.length + 3;
          }
        });
      }
      const rowValues: string[] = [];
      fieldsToFill.forEach((fieldkey, fieldnumber) => {
        let inputvalue = rowdata[fieldkey];
        if (dateFields.includes(fieldkey) && inputvalue) {
          inputvalue = new Date(inputvalue).toLocaleDateString('de-DE');
        } else if (timeStampFields.includes(fieldkey) && inputvalue) {
          inputvalue = this.getTimeStamp(new Date(inputvalue));
        }
        rowValues[fieldnumber + 1] = inputvalue;

        const headercol = worksheet.getColumn(fieldnumber + 1);
        if (inputvalue && headercol.width && inputvalue.toString().length > headercol.width) {
          headercol.width = inputvalue.length + 3;
        }
      });
      worksheet.addRow(rowValues);
    });
    workbook.xlsx.writeBuffer().then((buffer) => {
      saveAs(new Blob([buffer], { type: 'application/octet-stream' }), this.generateFileName(fileName));
    });
  }

  generateFileName(fileName: string): string {
    return formatDate(new Date(), 'yyyyMMdd_', 'de-DE') + fileName + '.xlsx';
  }

  getTimeStamp(date: Date) {
    const hours = String(date.getHours()).padStart(2, '0'); // Get hours and pad with leading zero if needed
    const minutes = String(date.getMinutes()).padStart(2, '0'); // Get minutes and pad with leading zero if needed
    return `${hours}:${minutes}`;
  }
}
